import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Plus, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { trophiesService, type ClassOwner } from '../../services/trophies';
import { useGymAdmin } from '../context/gym-admin-context';
import type { CategoryStrategyRule, CategoryStrategyState, TrophyCategory } from '../../types/trophy';

/** Radix Select cannot hold an empty value, so "none" carries a token. */
const NONE = 'none';

interface CategoryStrategyCardProps {
  slug: string;
  owner: ClassOwner;
  /** The categories on the page, which the table's lines are matched against. */
  categories: TrophyCategory[];
  /** The ones "create the missing categories" just wrote, for the list above. */
  onCategoriesCreated: (created: TrophyCategory[]) => void;
}

/**
 * How the circuit's entrants are put in a class without the desk: one of the
 * strategies the server knows (a federation's table of classes by year of
 * birth), or none.
 *
 * The table is drawn against the categories the organiser keeps, matched by
 * name, so a line with no category behind it says so — and the missing ones
 * can be written in one go, with exactly the names the table looks for.
 */
export default function CategoryStrategyCard({
  slug,
  owner,
  categories,
  onCategoriesCreated,
}: CategoryStrategyCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();

  const [state, setState] = useState<CategoryStrategyState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  // Read again whenever a category is added, renamed or removed: that is what
  // decides which lines of the table have a category behind them. Neither `t`
  // nor `toast` is a dependency — a failure goes to state — so a re-render
  // cannot turn into a re-fetch.
  const categoriesKey = categories.map((category) => `${category.id}:${category.name}`).join('|');

  useEffect(() => {
    let cancelled = false;
    trophiesService
      .getCategoryStrategy(slug, owner)
      .then((next) => {
        if (cancelled) return;
        setState(next);
        setLoadFailed(false);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, owner, categoriesKey]);

  const selected = state?.strategies.find((option) => option.key === state.strategy) ?? null;
  const missing = selected?.rules.filter((rule) => rule.category_id === null) ?? [];

  const handleChange = async (value: string) => {
    setSaving(true);
    try {
      setState(await trophiesService.setCategoryStrategy(slug, value === NONE ? null : value, owner));
    } catch (error) {
      toast({
        title: t('gymAdmin.categories.strategy.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // One at a time, so a refusal halfway still hands the page the ones that
  // were written.
  const handleCreateMissing = async () => {
    setCreating(true);
    const created: TrophyCategory[] = [];
    try {
      for (const rule of missing) {
        created.push(await trophiesService.createCategory(slug, rule.name, owner));
      }
    } catch (error) {
      toast({
        title: t('gymAdmin.categories.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      if (created.length > 0) onCategoriesCreated(created);
      setCreating(false);
    }
  };

  const bornLabel = (rule: CategoryStrategyRule) => {
    if (rule.born_from !== null && rule.born_to !== null) {
      return rule.born_from === rule.born_to ? String(rule.born_from) : `${rule.born_from}–${rule.born_to}`;
    }
    if (rule.born_to !== null) return t('gymAdmin.categories.strategy.bornUntil', { year: rule.born_to });
    if (rule.born_from !== null) return t('gymAdmin.categories.strategy.bornFrom', { year: rule.born_from });
    return t('gymAdmin.categories.strategy.bornAny');
  };

  return (
    <Card data-testid="category-strategy">
      <CardHeader>
        <CardTitle>{t('gymAdmin.categories.strategy.title')}</CardTitle>
        <CardDescription>{t('gymAdmin.categories.strategy.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadFailed && (
          <p className="text-sm text-destructive">{t('gymAdmin.categories.strategy.loadError')}</p>
        )}

        {!state && !loadFailed && <Skeleton className="h-10 w-full" />}

        {state && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="category-strategy">{t('gymAdmin.categories.strategy.label')}</Label>
              <div className="flex items-center gap-2">
                <Select value={state.strategy ?? NONE} onValueChange={handleChange} disabled={saving}>
                  <SelectTrigger id="category-strategy" className="w-full sm:w-96">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('gymAdmin.categories.strategy.none')}</SelectItem>
                    {state.strategies.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {t(`gymAdmin.categories.strategy.options.${option.key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {saving && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
              </div>
            </div>

            {selected && (
              <>
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t('gymAdmin.categories.strategy.table.category')}</th>
                        <th className="px-3 py-2 font-medium">{t('gymAdmin.categories.strategy.table.born')}</th>
                        <th className="px-3 py-2 font-medium">{t('gymAdmin.categories.strategy.table.gender')}</th>
                        <th className="px-3 py-2 font-medium">
                          <span className="sr-only">{t('gymAdmin.categories.strategy.table.status')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.rules.map((rule) => (
                        <tr key={rule.name} className="border-t" data-testid="strategy-rule">
                          <td className="px-3 py-2 font-medium">{rule.name}</td>
                          <td className="px-3 py-2 tabular-nums">{bornLabel(rule)}</td>
                          <td className="px-3 py-2">
                            {t(`gymAdmin.categories.strategy.gender.${rule.gender ?? 'mixed'}`)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {rule.category_id ? (
                              <Check
                                className="ml-auto size-4 text-green-600"
                                aria-label={t('gymAdmin.categories.strategy.found')}
                              />
                            ) : (
                              <span className="text-xs font-medium text-amber-600">
                                {t('gymAdmin.categories.strategy.missing')}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {missing.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                      {t('gymAdmin.categories.strategy.missingCount', { count: missing.length })}
                    </p>
                    <Button variant="outline" onClick={handleCreateMissing} disabled={creating} className="gap-2">
                      {creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                      {t('gymAdmin.categories.strategy.createMissing', { count: missing.length })}
                    </Button>
                  </div>
                )}

                {!state.gender_collected && (
                  <div
                    className="flex flex-wrap items-start gap-3 rounded-lg border border-amber-500/50 p-3 text-sm"
                    data-testid="strategy-gender-not-collected"
                  >
                    <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    <p className="min-w-0 flex-1">{t('gymAdmin.categories.strategy.genderNotCollected')}</p>
                    {owner === 'trophy' && (
                      <Button size="sm" variant="outline" onClick={() => navigate(placePath('/dpa'))}>
                        {t('gymAdmin.categories.strategy.toDpa')}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
